
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { Button } from '../../components/ui/button';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { ArrowLeft, Save, Download, User as UserIcon, Phone as PhoneIcon, Mail as MailIcon } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useJob } from '../../context/JobContext';

const normalizeCustomerLabel = (customer) => {
  if (!customer) return '';
  const label = String(customer.full_name || '').trim();
  if (label) return label;
  if (customer.id) return `Cliente #${customer.id}`;
  return '';
};

const sanitizeFilename = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'modello';
  const normalized = raw
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return (normalized || 'modello').slice(0, 80).toLowerCase();
};

export default function ModulisticaEdit() {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { addMonitoringNotification } = useJob();
  
  const [contentTab, setContentTab] = useState(0);
  const [formData, setFormData] = useState({
    nome_modello: '',
    content: '',
    data_validita: '',
    categorie_ids: []
  });
  
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [testCustomerOptions, setTestCustomerOptions] = useState([]);
  const [testCustomerLoading, setTestCustomerLoading] = useState(false);
  const [testCustomer, setTestCustomer] = useState(null);
  const [testCustomerInputValue, setTestCustomerInputValue] = useState('');
  const [testRenderLoading, setTestRenderLoading] = useState(false);
  const [testRenderedHtml, setTestRenderedHtml] = useState('');

  const modules = {
    toolbar: [
      [{ 'font': [] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'align': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'font',
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'align',
    'list', 'bullet', 'indent',
    'blockquote', 'code-block',
    'link', 'image'
  ];

  useEffect(() => {
    fetchCategorie();
    if (isEditMode) {
      fetchModello();
    }
  }, [id]);

  useEffect(() => {
    if (contentTab !== 1) return;
    const timer = setTimeout(async () => {
      const q = String(testCustomerInputValue || '').trim();
      if (!q) {
        setTestCustomerOptions(testCustomer?.id ? [testCustomer] : []);
        return;
      }
      setTestCustomerLoading(true);
      try {
        const res = await api.get('/modulistica/test-customers', { params: { q, limit: 20 } });
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        const merged = testCustomer?.id ? [...list, testCustomer] : list;
        const byId = new Map();
        merged.forEach((c) => {
          if (c?.id) byId.set(String(c.id), c);
        });
        setTestCustomerOptions(Array.from(byId.values()));
      } catch (err) {
        setTestCustomerOptions(testCustomer?.id ? [testCustomer] : []);
      } finally {
        setTestCustomerLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contentTab, testCustomerInputValue, testCustomer?.id]);

  useEffect(() => {
    if (contentTab !== 1) return;
    if (!testCustomer?.id) {
      setTestRenderedHtml('');
      return;
    }
    const timer = setTimeout(async () => {
      setTestRenderLoading(true);
      try {
        const res = await api.post('/modulistica/test-render', {
          content: formData.content,
          customer_id: Number(testCustomer.id)
        });
        setTestRenderedHtml(String(res.data?.data?.rendered || ''));
      } catch (err) {
        setTestRenderedHtml('');
        toast.error(err.response?.data?.msg || 'Errore nella generazione del test');
      } finally {
        setTestRenderLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contentTab, formData.content, testCustomer?.id]);

  const fetchCategorie = async () => {
    try {
      const response = await api.get('/categorie-utilizzo');
      setCategorie(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error('Errore nel caricamento delle categorie');
    }
  };

  const fetchModello = async () => {
    try {
      const response = await api.get(`/modulistica/${id}`);
      const data = response.data;
      setFormData({
        nome_modello: data.nome_modello,
        content: data.content || '',
        data_validita: data.data_validita ? data.data_validita.split('T')[0] : '',
        categorie_ids: data.categorie_ids || []
      });
    } catch (err) {
      console.error('Error fetching modello:', err);
      toast.error('Errore nel caricamento del modello');
      navigate('/modulistica/modelli');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome_modello) {
      toast.error('Il nome del modello è obbligatorio');
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        await api.put(`/modulistica/${id}`, formData);
        toast.success('Modello aggiornato con successo');
      } else {
        await api.post('/modulistica', formData);
        toast.success('Modello creato con successo');
      }
      navigate('/modulistica/modelli');
    } catch (err) {
      console.error('Error saving modello:', err);
      toast.error(err.response?.data?.msg || 'Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTestPdf = async () => {
    if (!testCustomer?.id) return;
    try {
      const filename = `test_${sanitizeFilename(formData.nome_modello)}_${sanitizeFilename(normalizeCustomerLabel(testCustomer))}.pdf`;
      addMonitoringNotification({
        kind: 'modulistica_test_pdf',
        filename,
        nomeModello: formData.nome_modello,
        customerId: Number(testCustomer.id),
        customerName: normalizeCustomerLabel(testCustomer),
        content: formData.content
      });
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Errore nel download PDF');
    }
  };

  if (loadingData) {
    return (
      <Box className="flex justify-center items-center h-screen">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/modulistica/modelli')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <Typography variant="h4" className="font-bold text-gray-800">
            {isEditMode ? 'Modifica Modello' : 'Nuovo Modello'}
          </Typography>
        </div>
      </div>

      <Card className="p-6 shadow-md border-none">
        <form onSubmit={handleSubmit} className="space-y-6">
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Nome Modello"
                value={formData.nome_modello}
                onChange={(e) => setFormData({ ...formData, nome_modello: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Data Validità"
                InputLabelProps={{ shrink: true }}
                value={formData.data_validita}
                onChange={(e) => setFormData({ ...formData, data_validita: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={categorie}
                getOptionLabel={(option) => option.nome_categoria}
                value={categorie.filter(cat => formData.categorie_ids.includes(cat.id))}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    categorie_ids: newValue.map(item => item.id)
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Categorie" placeholder="Seleziona categorie..." />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            <Grid item xs={12}>
              <div className="flex justify-between items-center mb-2">
                <Typography variant="subtitle2">
                  Contenuto Modello
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Placeholder disponibili: {'{cliente}'}, {'{data_nascita}'}, {'{luogo_nascita}'}, {'{luogo_residenza}'}, {'{codice_fiscale}'}, {'{email}'}, {'{telefono}'}, {'{data_corrente}'}, {'{prospetto_contratti}'}, {'{prospetto_pratiche}'}, {'{prospetto_rateazioni}'}, {'{prospetto_fatture}'}, {'{prospetto_incassi}'}
                </Typography>
              </div>
              <Tabs value={contentTab} onChange={(_e, v) => setContentTab(v)} sx={{ mb: 2 }}>
                <Tab label="Modello" />
                <Tab label="Test modello" />
              </Tabs>

              {contentTab === 0 ? (
                <div className="h-96 mb-12">
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    className="h-full"
                    modules={modules}
                    formats={formats}
                  />
                </div>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <Autocomplete
                        size="small"
                        disablePortal
                        options={Array.isArray(testCustomerOptions) ? testCustomerOptions : []}
                        value={testCustomer}
                        loading={testCustomerLoading}
                        onChange={(_e, v) => {
                          setTestCustomer(v);
                          setTestCustomerInputValue(v ? normalizeCustomerLabel(v) : '');
                          if (v?.id != null) {
                            setTestCustomerOptions((prev) => {
                              const merged = [...(Array.isArray(prev) ? prev : []), v];
                              const byId = new Map();
                              merged.forEach((c) => {
                                if (c?.id) byId.set(String(c.id), c);
                              });
                              return Array.from(byId.values());
                            });
                          }
                        }}
                        onInputChange={(_event, value) => setTestCustomerInputValue(value)}
                        inputValue={testCustomerInputValue}
                        filterOptions={(options) => options}
                        isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                        getOptionLabel={(option) => normalizeCustomerLabel(option)}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} sx={{ py: 1, alignItems: 'flex-start' }}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: '#eef2ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                color: 'primary.main',
                                mt: 0.25
                              }}
                            >
                              <UserIcon size={18} />
                            </Box>
                            <Box sx={{ ml: 1.5, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {normalizeCustomerLabel(option)}
                              </Typography>
                              {option?.telephone ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                                  <PhoneIcon size={14} />
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    {option.telephone}
                                  </Typography>
                                </Box>
                              ) : null}
                              {option?.email ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                                  <MailIcon size={14} />
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    {option.email}
                                  </Typography>
                                </Box>
                              ) : null}
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Cliente"
                            placeholder="Nome cliente..."
                            helperText={
                              testCustomer ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                  {testCustomer?.telephone ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <PhoneIcon size={13} />
                                      <Typography variant="caption" color="text.secondary">
                                        {testCustomer.telephone}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                  {testCustomer?.email ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                      <MailIcon size={13} />
                                      <Typography variant="caption" color="text.secondary">
                                        {testCustomer.email}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                </Box>
                              ) : (
                                'Lookup remoto sulla tabella customer'
                              )
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {testCustomerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        type="button"
                        variant="outline"
                        className="gap-2"
                        disabled={!testCustomer?.id || testRenderLoading}
                        onClick={handleDownloadTestPdf}
                      >
                        <Download className="h-4 w-4" />
                        Crea PDF (notifiche)
                      </Button>
                    </Grid>
                  </Grid>

                  {testRenderLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        Generazione anteprima...
                      </Typography>
                    </Box>
                  ) : testCustomer?.id ? (
                    testRenderedHtml ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          borderRadius: '16px',
                          p: 2,
                          maxHeight: 420,
                          overflow: 'auto',
                          '& img': { maxWidth: '100%' }
                        }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: testRenderedHtml }} />
                      </Paper>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Nessun contenuto generato
                      </Typography>
                    )
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Seleziona un cliente per visualizzare il modello con i placeholder sostituiti
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/modulistica/modelli')}>
              Annulla
            </Button>
            <Button type="submit" disabled={loading} className="gap-2">
              <Save className="h-4 w-4" />
              {loading ? 'Salvataggio...' : 'Salva Modello'}
            </Button>
          </div>
        </form>
      </Card>
    </Box>
  );
}
